import Fuse from 'fuse.js';
import { createSelector } from 'reselect';

import { removeArrayDuplicates } from 'utils/remove-duplicates';
import { blankArr, blankObj } from 'utils/fn';

import { getCatalogData } from '../selectors';
import { getCatalogOfficesIsLoading } from '../products/selectors';
import {
  DELIVERY_TAKEAWAY_POINTS_SHOW_COUNT,
  CATALOG_TAKEAWAY_VIEW_LIST,
  CATALOG_TAKEAWAY_VIEW_MAP,
  CATALOG_POINT_SORT_METRO,
  CATALOG_POINT_SORT_ADDR,
  CATALOG_POINT_GTM_VIEW_MAP,
  CATALOG_POINT_GTM_VIEW_LIST_SORT_METRO,
  CATALOG_POINT_GTM_VIEW_LIST_SORT_ADDR,
  CATALOG_POINT_GTM_VIEW_LIST_SORT_DATE,
} from './constants';

export const getCatalogTakeawayControls = (state) =>
  getCatalogData(state).takeawayControls || blankObj;
export const getCatalogDeliveryDetails = (state) =>
  getCatalogData(state).deliveryDetails || blankObj;
export const getCatalogProductsSEOList = (state) =>
  getCatalogData(state).productsSEOList || blankArr;
export const getProductImpressionsInfo = (state, id) =>
  getCatalogProductsSEOList(state).filter((impression) => impression.id === id)[0];
/**
 * DevicePlus
 */
export const getCatalogDevicePlusInfo = (state) => getCatalogData(state).bonusService || {};
export const getCatalogDevicePlusHTMLBlock = (state) =>
  getCatalogDevicePlusInfo(state).contentPromotionText;
export const getCatalogDevicePlusFinalHeader = (state) =>
  getCatalogDevicePlusInfo(state).finalHeader || null;
export const getCatalogDevicePlusFinalText = (state) =>
  getCatalogDevicePlusInfo(state).finalText || null;

export const getProductPointsUrl = (state) => getCatalogDeliveryDetails(state).productPointsUrl;

export const getCatalogDeliveryPickupBuyData = (state) =>
  getCatalogTakeawayControls(state).pickupBuy || blankObj;

export const getCatalogDeliveryAgreementUrl = (state) =>
  getCatalogDeliveryPickupBuyData(state).agreementDataUrl;

export const getCatalogDeliveryAgreementContent = (state) =>
  getCatalogDeliveryPickupBuyData(state).agreementContent;

export const getCatalogDeliveryPropsContent = (state) =>
  getCatalogDeliveryPickupBuyData(state).propsContent;

export const getCatalogDeliveryPropsUrl = (state) =>
  getCatalogDeliveryPickupBuyData(state).propsDataUrl;

export const getCatalogTakeawayData = (state) => getCatalogDeliveryDetails(state).takeaway;
export const getCatalogTakeawayViews = (state) => getCatalogTakeawayData(state).views || blankArr;

export const getCatalogTakeawayOpenState = (state) =>
  getCatalogTakeawayControls(state).deliveryPickupOpenState;

export const getCatalogTakeawayPoint = (state) =>
  getCatalogTakeawayControls(state).deliveryPickupPoint;

export const getCatalogTakeawayViewState = (state) =>
  getCatalogTakeawayControls(state).deliveryPickupViewState;

export const getCatalogHoveredProductId = (state) =>
  getCatalogTakeawayControls(state).hoveredProductId;

export const getCatalogHoveredProductTitle = (state) =>
  getCatalogTakeawayControls(state).hoveredProductTitle;

export const getCatalogTakeawaySuccess = (state) =>
  getCatalogTakeawayControls(state).deliveryPickupSuccess || {};
export const getCatalogTakeawayLoading = (state) =>
  getCatalogTakeawayControls(state).deliveryPickupLoading;
export const getCatalogTakeawayErrorDetails = (state) =>
  getCatalogTakeawayControls(state).deliveryPickupErrorDetails;
export const getCatalogTakeawayPointsViewSlug = (state) =>
  getCatalogTakeawayControls(state).deliveryPointsViewForm;

/**
 * SortWidget
 */

export const getCatalogSortState = (state) =>
  getCatalogTakeawayControls(state).sortWidgetState || blankObj;

export const getCatalogSortActiveButton = (state) => getCatalogSortState(state).activeButton;
export const getCatalogSortReverseState = (state) => getCatalogSortState(state).reverse;
export const getCatalogSortButtons = (state) => getCatalogSortState(state).usingButtons || [];
export const getCatalogShowPointsCount = (state) =>
  getCatalogTakeawayControls(state).officesToShow || DELIVERY_TAKEAWAY_POINTS_SHOW_COUNT;

/**
 * GTM
 */

export const getTakeawayGTMPointType = createSelector(
  getCatalogTakeawayPointsViewSlug,
  getCatalogSortActiveButton,
  (view, sortBy) => {
    switch (view) {
      case CATALOG_TAKEAWAY_VIEW_MAP: {
        return CATALOG_POINT_GTM_VIEW_MAP;
      }
      case CATALOG_TAKEAWAY_VIEW_LIST: {
        if (sortBy === CATALOG_POINT_SORT_METRO) {
          return CATALOG_POINT_GTM_VIEW_LIST_SORT_METRO;
        } else if (sortBy === CATALOG_POINT_SORT_ADDR) {
          return CATALOG_POINT_GTM_VIEW_LIST_SORT_ADDR;
        }
        return CATALOG_POINT_GTM_VIEW_LIST_SORT_DATE;
      }
      default:
        return CATALOG_POINT_GTM_VIEW_LIST_SORT_METRO;
    }
  },
);

export const getCatalogTakeawayAvailability = (state) => !!getCatalogTakeawayData(state);
export const getCatalogProductOfficesListAvailability = (state) =>
  !!getCatalogTakeawayControls(state).offices;
export const getCatalogPaymentPointText = (state) =>
  getCatalogTakeawaySettings(state).paymentPointText || '';

/** Filters for delivery takeaway */
export const getCatalogTakeawaySettings = (state) => getCatalogTakeawayData(state).settings || {};
const getCatalogTakeawayAllOfficesRaw = (state) =>
  getCatalogProductOfficesListAvailability(state) ?
    getCatalogTakeawayControls(state).allOffices
  : [];

export const getCatalogTakeawayAllOffices = createSelector(
  getCatalogTakeawayAllOfficesRaw,
  (officesRaw) => removeArrayDuplicates(officesRaw || [], (a, b) => a.pointCode === b.pointCode),
);

export const getCatalogTakeawayPins = (state) => getCatalogTakeawaySettings(state).points || {};
export const getCatalogTakeawayInitialFilters = (state) =>
  getCatalogTakeawayAvailability(state) ? getCatalogTakeawaySettings(state).filters : [];
export const getCatalogTakeawayFilters = (state) => getCatalogTakeawayControls(state).filters || [];
export const getCatalogTakeawayActiveFilters = createSelector(
  getCatalogTakeawayFilters,
  (filters) => filters.filter((item) => item.checked).map((item) => item.slug),
);

export const getCatalogTakeawayFiltersToShow = createSelector(
  getCatalogOfficesIsLoading,
  getCatalogTakeawayAllOffices,
  getCatalogTakeawayInitialFilters,
  (loading, list, filters) => {
    if (loading && !list) {
      return null;
    }
    const firstUniqueByTypeOffices = (list || []).filter(
      (
        (set) => (office) =>
          !set.has(office.ptype) && set.add(office.ptype)
      )(new Set()),
    );

    const filtersToShow = firstUniqueByTypeOffices.map((office) => {
      const filter = filters.find((filt) => office.ptype === filt.slug);
      const newFilter = { ...filter };
      newFilter.title = newFilter.title.replace(`pr_${office.ptype}`, office.price);
      return newFilter;
    });
    return filtersToShow.length > 1 ? filtersToShow : null;
  },
);

export const getCatalogTakeawayFiltersLoading = (state) => !getCatalogTakeawayFiltersToShow(state);

export const getCatalogTakeawayFilteredOffices = createSelector(
  getCatalogTakeawayAllOffices,
  getCatalogTakeawayActiveFilters,
  getCatalogTakeawayFiltersToShow,
  (offices, activeFilters, filtersToShow) =>
    (filtersToShow || []).length !== 0 ?
      offices.filter((office) => activeFilters.includes(office.ptype))
    : offices,
);

export const getCatalogTakeawayFilterHeaders = (state) =>
  getCatalogTakeawayAvailability(state) ? getCatalogTakeawaySettings(state).headers : [];

/**
 * Using Fuse, selector finds the best matches, returns empty list when query is empty
 */

export const getCatalogProductOfficesListRaw = (state) => getCatalogTakeawayControls(state).offices;

const getCatalogProductOfficesFused = createSelector(
  getCatalogTakeawayFilteredOffices,
  (list) =>
    new Fuse(list, {
      includeScore: true,
      shouldSort: true,
      threshold: 0.4,
      location: 0,
      tokenize: false,
      distance: 300,
      maxPatternLength: 32,
      minMatchCharLength: 2,
      keys: ['address', 'subway.name'],
    }),
);

export const getCatalogTakeawaySearchQuery = (state) =>
  getCatalogTakeawayControls(state).deliveryTakeawaySearchQuery || '';

/** to make fuse work better, we trim query string */
export const getCatalogTakeawaySearchQueryTrimmed = (state) =>
  getCatalogTakeawaySearchQuery(state).replace(/^\s*(.*?)\s*$/, '$1');

export const getCatalogOfficesListUnsorted = createSelector(
  getCatalogTakeawayFilteredOffices,
  getCatalogProductOfficesFused,
  getCatalogTakeawaySearchQueryTrimmed,
  (listRaw, listFused, query) => {
    if (query) {
      const list = listFused.search(query);
      const bestScore = list.reduce((minScore, item) => Math.min(item.score, minScore), 1);
      return list
        .filter(({ score }) => score <= bestScore * 2) // only take results that are 3x worse than original;
        .map(({ item }) => item);
    }
    return listRaw;
  },
);

export const getCatalogOfficesListSortFunc = createSelector(
  getCatalogSortActiveButton,
  getCatalogSortReverseState,
  (sortBy, reverse) => {
    switch (sortBy) {
      case 'date': {
        return null;
      }
      case 'address': {
        return (a, b) => {
          // we usin 'z' as no data parameter in order to push items without data to end of array
          const addressFromA = (a.address || 'яяяя').toString().toLowerCase();
          const addressFromB = (b.address || 'яяяя').toString().toLowerCase();
          return reverse ?
              -addressFromA.localeCompare(addressFromB)
            : addressFromA.localeCompare(addressFromB);
        };
      }
      case 'metro': {
        return (a, b) => {
          const metroFromA = ((a.subway || {}).name || 'яяяя').toString().toLowerCase();
          const metroFromB = ((b.subway || {}).name || 'яяяя').toString().toLowerCase();
          return reverse ?
              -metroFromA.localeCompare(metroFromB)
            : metroFromA.localeCompare(metroFromB);
        };
      }
      default:
        return null;
    }
  },
);

export const getCatalogFilteredOfficesCount = (state) =>
  (getCatalogOfficesListUnsorted(state) || []).length;

/**
 * Using current state of sort widget for compare function
 */
export const getCatalogProductOfficesList = createSelector(
  getCatalogOfficesListUnsorted,
  getCatalogOfficesListSortFunc,
  (listUnsorted, sortFunc) =>
    sortFunc ? (listUnsorted || []).slice().sort(sortFunc) : listUnsorted,
);
